import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  BellIcon,
  CheckCheck,
  LayoutDashboard,
  GraduationCap,
  Library,
  Newspaper,
  Images,
  Tags,
  ClipboardList,
  Users,
  Home,
  Menu as MenuIcon,
  X,
  ChevronDown,
  LogOut,
  UserRound,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useState, useEffect, useMemo } from "react";
import useNotifications from "../../hooks/useNotifications";
import NotificationCard from "../../components/common/NotificationCard";
import coursesService from "../../services/CoursesService";

const PRIMARY_NAV = [
  { label: "Inicio", path: "/admin/main", icon: LayoutDashboard, end: true },
  { label: "Cursos", path: "/admin/courses", icon: GraduationCap },
  { label: "Repositorio", path: "/admin/repository", icon: Library },
  { label: "Publicaciones", path: "/admin/publicaciones", icon: Newspaper },
  { label: "Comunidad", path: "/admin/CoursesAssing", icon: Users },
];

const SECONDARY_NAV = [
  { label: "Banners", path: "/admin/banners", icon: Images },
  { label: "Etiquetas", path: "/admin/label-groups", icon: Tags },
  { label: "Cuestionarios", path: "/admin/questionaires", icon: ClipboardList },
];

const ALL_NAV = [...PRIMARY_NAV, ...SECONDARY_NAV];

function isNavActive(pathname, item) {
  if (item.end || item.path === "/admin/main") {
    return pathname === "/admin/main" || pathname === "/admin";
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function AdminPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const mainLogo = "/img/logocom.png";
  const userId = JSON.parse(localStorage.getItem("usuarioConCedula") || "{}").id;
  const [notificationKey, setNotificationKey] = useState(0);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    error: notificationsError,
    isConnected,
    markAsRead,
    markAllAsRead,
    reconnect,
    toastNotification,
    dismissToast,
  } = useNotifications(
    `/api/Notification/${userId}`,
    `${process.env.REACT_APP_LOCATION}/notificationHub`,
    `${process.env.REACT_APP_LOCATION}`,
  );

  const activeSecondary = useMemo(
    () => SECONDARY_NAV.find((item) => isNavActive(location.pathname, item)),
    [location.pathname]
  );

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const newKey = Date.now() + notifications.length + unreadCount;
    setNotificationKey(newKey);
  }, [notifications.length, unreadCount]);

  useEffect(() => {
    if (notifications.length > 0 && isNotificationMenuOpen) {
      setIsNotificationMenuOpen(false);
      setTimeout(() => {
        setIsNotificationMenuOpen(true);
      }, 50);
    }
  }, [notifications.length]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").trim();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;

    markAsRead(notification.id);

    if (notification.courseId) {
      try {
        const usuarioConCedula = JSON.parse(localStorage.getItem("usuarioConCedula") || "{}");
        const userCourses = await coursesService.getUserCoursesWithCedula(usuarioConCedula, navigate);
        const isEnrolled = userCourses.some((course) => course.id === parseInt(notification.courseId));

        if (isEnrolled) {
          navigate(`/courses/Introcourse/${notification.courseId}`);
        } else {
          navigate(`/courses/getCourses?courseId=${notification.courseId}`);
        }
      } catch (error) {
        console.error("Error checking enrollment:", error);
        navigate("/courses/getCourses");
      }
    } else if (notification.message?.includes("repositorio")) {
      navigate("/admin/repository");
    }
  };

  const formatNotificationTime = (createdAt) => {
    const now = new Date();
    const notificationTime = new Date(createdAt);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

    if (diffInMinutes < 1) return "Ahora";
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "course_completion":
        return "🎓";
      case "new_course":
        return "📚";
      case "assignment":
        return "📝";
      case "reminder":
        return "⏰";
      case "system":
        return "🔔";
      default:
        return "💬";
    }
  };

  const handleConnectionRetry = async () => {
    try {
      await reconnect();
    } catch (error) {
      console.error("Error al reconectar:", error);
    }
  };

  const navLinkClass = (active) =>
    [
      "admin-nav-link inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all duration-200 no-underline whitespace-nowrap",
      active
        ? "admin-nav-link--active bg-[#4DABA4] shadow-md shadow-[#4DABA4]/25"
        : "admin-nav-link--idle text-slate-700 hover:bg-[#4DABA4]/12 hover:text-[#2f6f6a]",
    ].join(" ");

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isNavActive(location.pathname, item);
    return (
      <Link
        key={item.path}
        to={item.path}
        className={navLinkClass(active)}
        aria-current={active ? "page" : undefined}
        style={active ? { color: "#fff", backgroundColor: "#4DABA4" } : undefined}
      >
        <Icon
          className="h-4 w-4 shrink-0"
          aria-hidden="true"
          style={{ color: active ? "#fff" : "#4DABA4" }}
        />
        <span style={active ? { color: "#fff" } : undefined}>{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {toastNotification && (
        <NotificationCard
          notification={toastNotification}
          onClick={() => {
            setIsNotificationMenuOpen(true);
            dismissToast();
          }}
          onClose={() => {
            dismissToast();
          }}
        />
      )}

      <header className="sticky top-0 z-40 border-b border-[#4DABA4]/15 bg-white/90 backdrop-blur-md shadow-[0_8px_30px_rgba(77,171,164,0.08)]">
        <div className="h-1 w-full bg-gradient-to-r from-[#4DABA4] via-[#5BB4A8] to-[#88B24F]" />

        <nav className="mx-auto flex max-w-[1600px] items-center justify-between gap-3 px-3 py-2.5 sm:px-5">
          {/* Brand */}
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#4DABA4]/20 text-[#4DABA4] transition hover:bg-[#4DABA4]/10"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
            </button>

            <button
              type="button"
              onClick={() => navigate("/admin/main")}
              className="flex min-w-0 items-center gap-3 rounded-xl bg-transparent p-0 text-left transition hover:opacity-90"
            >
              <img src={mainLogo} alt="Mega Academia" className="h-11 w-auto object-contain sm:h-12" />
              <div className="hidden min-w-0 sm:block">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4DABA4]">
                  Administración
                </p>
                <p className="truncate text-sm font-bold text-slate-800">Panel de control</p>
              </div>
            </button>
          </div>

          {/* Desktop nav */}
          <div className="hidden items-center gap-1 xl:flex">
            {PRIMARY_NAV.map(renderNavItem)}

            <Menu as="div" className="relative">
              <MenuButton
                className={[
                  "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all duration-200",
                  activeSecondary
                    ? "bg-[#4DABA4] shadow-md shadow-[#4DABA4]/25"
                    : "text-slate-700 hover:bg-[#4DABA4]/12 hover:text-[#2f6f6a]",
                ].join(" ")}
                style={
                  activeSecondary
                    ? { color: "#fff", backgroundColor: "#4DABA4" }
                    : undefined
                }
              >
                <span style={activeSecondary ? { color: "#fff" } : undefined}>
                  {activeSecondary ? activeSecondary.label : "Más"}
                </span>
                <ChevronDown
                  className="h-4 w-4 opacity-80"
                  style={{ color: activeSecondary ? "#fff" : "currentColor" }}
                />
              </MenuButton>
              <MenuItems className="absolute left-0 z-20 mt-2 w-56 origin-top-left rounded-2xl border border-slate-100 bg-white p-2 shadow-xl focus:outline-none">
                {SECONDARY_NAV.map((item) => {
                  const Icon = item.icon;
                  const active = isNavActive(location.pathname, item);
                  return (
                    <MenuItem key={item.path}>
                      {({ focus }) => (
                        <Link
                          to={item.path}
                          className={[
                            "admin-nav-link flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium no-underline transition",
                            active
                              ? "admin-nav-link--active bg-[#4DABA4]"
                              : focus
                                ? "bg-[#4DABA4]/12 text-[#2f6f6a]"
                                : "text-slate-700",
                          ].join(" ")}
                          style={active ? { color: "#fff", backgroundColor: "#4DABA4" } : undefined}
                        >
                          <Icon
                            className="h-4 w-4 shrink-0"
                            style={{ color: active ? "#fff" : "#4DABA4" }}
                          />
                          <span style={active ? { color: "#fff" } : undefined}>{item.label}</span>
                        </Link>
                      )}
                    </MenuItem>
                  );
                })}
              </MenuItems>
            </Menu>
          </div>

          {/* Compact desktop (lg) scrollable strip */}
          <div className="hidden max-w-[46vw] items-center gap-1 overflow-x-auto scrollbar-none lg:flex xl:hidden">
            {ALL_NAV.map(renderNavItem)}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/"
              className="hidden items-center gap-2 rounded-xl border border-[#4DABA4]/30 bg-white px-3 py-2 text-sm font-semibold text-[#2f6f6a] no-underline transition hover:bg-[#4DABA4]/12 hover:text-[#2f6f6a] md:inline-flex"
            >
              <Home className="h-4 w-4 text-[#4DABA4]" />
              Vista estudiante
            </Link>

            {/* Notifications */}
            <Menu as="div" className="relative">
              <div>
                <MenuButton
                  onClick={() => setIsNotificationMenuOpen(!isNotificationMenuOpen)}
                  className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#4DABA4]/25 bg-white !text-[#4DABA4] transition hover:bg-[#4DABA4]/12 hover:!text-[#2f6f6a] focus:outline-none focus:ring-2 focus:ring-[#4DABA4]/40"
                >
                  <BellIcon className="h-5 w-5 !text-inherit" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold leading-none text-white">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </MenuButton>
              </div>
              <MenuItems
                static
                className={`absolute right-0 z-20 mt-3 max-h-[500px] w-[min(24rem,calc(100vw-1.5rem))] origin-top-right overflow-y-auto rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 transition-all duration-300 ease-in-out focus:outline-none ${
                  isNotificationMenuOpen
                    ? "pointer-events-auto scale-100 opacity-100"
                    : "pointer-events-none scale-95 opacity-0"
                }`}
              >
                <div className="py-2" key={notificationKey}>
                  <div className="flex items-center justify-between border-b border-[#4DABA4]/15 bg-gradient-to-r from-[#4DABA4]/10 to-[#88B24F]/5 px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 animate-pulse rounded-full bg-[#4DABA4]" />
                      <h3 className="text-base font-bold text-slate-800">Notificaciones</h3>
                      {!isConnected && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-600">
                          Sin conexión
                        </span>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        onClick={() => markAllAsRead(userId)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#4DABA4]/20 bg-[#4DABA4]/10 px-2.5 py-1 text-xs font-semibold text-[#4DABA4] transition hover:bg-[#4DABA4]/20"
                      >
                        <CheckCheck className="h-3 w-3" />
                        Marcar todas
                      </button>
                    )}
                  </div>

                  {notificationsError && (
                    <div className="mx-4 mt-3 rounded-xl border border-red-200 bg-red-50 p-4">
                      <p className="mb-1 text-sm font-semibold text-red-800">Error de conexión</p>
                      <p className="mb-3 text-xs text-red-600">{notificationsError}</p>
                      <button
                        type="button"
                        onClick={handleConnectionRetry}
                        className="rounded-md bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-200"
                      >
                        Reintentar conexión
                      </button>
                    </div>
                  )}

                  {notificationsLoading ? (
                    <div className="px-6 py-12 text-center">
                      <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#4DABA4]/20 border-t-[#4DABA4]" />
                      <p className="text-sm font-medium text-slate-600">Cargando notificaciones...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                        <BellIcon className="h-8 w-8 text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">No hay notificaciones</p>
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notification) => (
                      <div key={notification.id} className="px-3 py-1.5">
                        <button
                          type="button"
                          onClick={() => handleNotificationClick(notification)}
                          className={`w-full rounded-xl p-3.5 text-left transition hover:bg-slate-50 ${
                            !notification.isRead
                              ? "border-l-4 border-l-[#4DABA4] bg-[#4DABA4]/5"
                              : "border border-slate-100 bg-white"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#4DABA4]/15 text-lg">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex items-start justify-between gap-2">
                                <p
                                  className={`line-clamp-1 text-sm font-semibold ${
                                    !notification.isRead ? "text-[#4DABA4]" : "text-slate-900"
                                  }`}
                                >
                                  {notification.title}
                                </p>
                                {!notification.isRead && (
                                  <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#4DABA4]" />
                                )}
                              </div>
                              <p className="mb-2 line-clamp-2 text-sm text-slate-600">{notification.message}</p>
                              <div className="flex items-center justify-between gap-2">
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                                  {formatNotificationTime(notification.createdAt)}
                                </span>
                                {notification.courseName && (
                                  <span className="rounded-full border border-[#4DABA4]/20 bg-[#4DABA4]/10 px-2 py-0.5 text-[11px] font-medium text-[#4DABA4]">
                                    {stripHtml(notification.courseName)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    ))
                  )}

                  {notifications.length > 10 && (
                    <div className="border-t border-slate-100 px-5 py-3">
                      <Link
                        to="/notifications"
                        className="flex w-full items-center justify-center rounded-xl border border-[#4DABA4]/20 bg-white px-4 py-2 text-sm font-semibold text-[#4DABA4] no-underline transition hover:bg-[#4DABA4]/10"
                      >
                        Ver todas ({notifications.length})
                      </Link>
                    </div>
                  )}
                </div>
              </MenuItems>
            </Menu>

            {/* User */}
            <Menu as="div" className="relative">
              <MenuButton className="inline-flex items-center gap-2 rounded-full border border-[#4DABA4]/20 bg-white p-1 pr-2 transition hover:border-[#4DABA4]/40 focus:outline-none focus:ring-2 focus:ring-[#4DABA4]/30">
                <img src="/img/user.png" alt="Usuario" className="user-avatar h-8 w-8 rounded-full object-cover" />
                <ChevronDown className="hidden h-4 w-4 text-slate-500 sm:block" />
              </MenuButton>
              <MenuItems className="absolute right-0 z-20 mt-2 w-52 origin-top-right rounded-2xl border border-slate-100 bg-white p-1.5 shadow-xl focus:outline-none">
                <MenuItem>
                  {({ focus }) => (
                    <Link
                      to="/profile/edit"
                      className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm no-underline ${
                        focus ? "bg-slate-50 text-[#4DABA4]" : "text-slate-700"
                      }`}
                    >
                      <UserRound className="h-4 w-4" />
                      Ver perfil
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={handleLogout}
                      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm ${
                        focus ? "bg-red-50 text-red-600" : "text-slate-700"
                      }`}
                    >
                      <LogOut className="h-4 w-4" />
                      Cerrar sesión
                    </button>
                  )}
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </nav>

        {/* Mobile panel */}
        {mobileOpen && (
          <div className="border-t border-[#4DABA4]/10 bg-white px-3 pb-4 pt-2 lg:hidden">
            <div className="grid gap-1">
              {ALL_NAV.map((item) => {
                const Icon = item.icon;
                const active = isNavActive(location.pathname, item);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={[
                      "admin-nav-link flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold no-underline transition",
                      active
                        ? "admin-nav-link--active bg-[#4DABA4]"
                        : "text-slate-700 hover:bg-[#4DABA4]/12 hover:text-[#2f6f6a]",
                    ].join(" ")}
                    style={active ? { color: "#fff", backgroundColor: "#4DABA4" } : undefined}
                  >
                    <Icon
                      className="h-4 w-4 shrink-0"
                      style={{ color: active ? "#fff" : "#4DABA4" }}
                    />
                    <span style={active ? { color: "#fff" } : undefined}>{item.label}</span>
                  </Link>
                );
              })}
              <Link
                to="/"
                className="mt-1 flex items-center gap-3 rounded-xl border border-[#4DABA4]/25 bg-white px-3 py-3 text-sm font-semibold text-[#2f6f6a] no-underline"
              >
                <Home className="h-4 w-4 text-[#4DABA4]" />
                Ir a vista estudiante
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto min-h-[calc(100vh-5rem)] max-w-[1600px] flex-1 p-4 sm:p-6">
        <Outlet />
      </main>
    </>
  );
}

export default AdminPanel;
